// Scoped presentation for first-person: no package markup or action handlers are replaced.
export function createFirstPersonOrientationUi(stage, visible, isVisible) {
  const root = document.querySelector(`[data-resource="${stage}"][data-relation="main"]`)
  const scroller = root?.closest('main')
  const scrollBehavior = scroller?.style.getPropertyValue('scroll-behavior') || ''
  const scrollPriority = scroller?.style.getPropertyPriority('scroll-behavior') || ''
  let list = null
  let listGeometry = null
  let proxy = null
  let menu = null
  let panel = null
  let held = false
  let menuObserver = null
  let onMenu = null
  let guardedAction = null
  let originalTabindex = null

  function top() {
    if (!scroller) return
    // Driver's behavior:auto otherwise inherits main's CSS scroll-smooth.
    scroller.style.setProperty('scroll-behavior', 'auto', 'important')
    scroller.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }

  function clearList() {
    proxy?.remove()
    proxy = list = listGeometry = null
  }

  function listElement(element) {
    return element === list && proxy?.isConnected ? proxy : element
  }

  function prepareList(step) {
    const table = step?.list ? (list && step.element === proxy ? list : step.element) : null
    if (table !== list) clearList()
    list = table
    if (!list) return false
    const bounds = list.getBoundingClientRect()
    const viewport = scroller?.getBoundingClientRect()
    const header = document.querySelector('header')?.getBoundingClientRect()
    const bar = visible('[data-test="resource-mobile-actions"]', root)
    // Leave room for Driver's default 10px stage padding. The mobile bar can
    // still be sliding in, so reserve its height before the transition ends.
    const top = Math.max(10, viewport?.top || 0, header?.bottom || 0) + 10
    const bottom = Math.min(innerHeight, viewport?.bottom || innerHeight,
      bar ? Math.min(bar.getBoundingClientRect().top, innerHeight - bar.offsetHeight) : innerHeight) - 10
    const rect = {
      top: Math.max(bounds.top, top),
      bottom: Math.min(bounds.bottom, bottom),
      left: Math.max(bounds.left, 10),
      right: Math.min(bounds.right, innerWidth - 10)
    }
    if (rect.bottom <= rect.top || rect.right <= rect.left) return null
    const geometry = [...Object.values(rect), innerWidth, innerHeight].join(',')
    const geometryChanged = geometry !== listGeometry
    listGeometry = geometry
    if (rect.top === bounds.top && rect.bottom === bounds.bottom && rect.left === bounds.left && rect.right === bounds.right) {
      const changed = !!proxy || geometryChanged
      proxy?.remove()
      proxy = null
      return changed
    }
    if (!proxy) {
      proxy = document.createElement('div')
      proxy.dataset.test = 'first-person.list-proxy'
      proxy.dataset.tourListProxy = stage
      proxy.setAttribute('role', 'presentation')
      proxy.style.position = 'fixed'
      proxy.style.pointerEvents = 'none'
      document.body.appendChild(proxy)
    }
    let changed = geometryChanged
    for (const [property, value] of Object.entries({ top: rect.top, left: rect.left, width: rect.right - rect.left, height: rect.bottom - rect.top })) {
      if (proxy.style[property] !== `${value}px`) {
        proxy.style[property] = `${value}px`
        changed = true
      }
    }
    return changed
  }

  function menuTrigger() {
    return root?.isConnected ? visible('[x-data="actionsDropdown()"] [x-ref="button"]', root) : null
  }

  function data() { return menu?.isConnected && window.Alpine?.$data(menu) }

  function ensureMenu() {
    if (!held) return true
    const trigger = menuTrigger()
    if (trigger && !menu.contains(trigger)) {
      closeMenu()
      return openMenu() && ensureMenu()
    }
    const state = data()
    if (!state) return false
    if (!state.open) state.open = true
    return isVisible(panel) && isVisible(visible('[data-action-name="create"]', panel))
  }

  function guardAction(element, key) {
    if (guardedAction) {
      if (originalTabindex === null) guardedAction.removeAttribute('tabindex')
      else guardedAction.setAttribute('tabindex', originalTabindex)
    }
    guardedAction = null
    if (!['status_create', 'joining'].includes(key)) return
    guardedAction = element
    originalTabindex = element.getAttribute('tabindex')
    // Driver also excludes its pointer-disabled active element from the Tab trap.
    element.setAttribute('tabindex', '-1')
  }

  function closeMenu() {
    held = false
    menuObserver?.disconnect()
    menuObserver = null
    if (!menu) return
    menu.dispatchEvent(new CustomEvent('guided-tour-menu', { detail: { open: false } }))
    menu.removeEventListener('guided-tour-menu', onMenu)
    menu.removeAttribute('data-guided-tour-menu')
    menu.removeAttribute('data-guided-tour-menu-open')
    menu = panel = onMenu = null
  }

  function openMenu() {
    if (held) return ensureMenu()
    const trigger = menuTrigger()
    menu = trigger?.closest('[x-data="actionsDropdown()"]')
    panel = menu?.querySelector('[x-ref="panel"]')
    if (!menu || !panel || !data()) return false
    onMenu = event => {
      const state = data()
      if (state) state.open = event.detail.open
    }
    menu.addEventListener('guided-tour-menu', onMenu)
    menu.setAttribute('data-guided-tour-menu', '')
    menu.setAttribute('data-guided-tour-menu-open', '')
    held = true
    // Package click.outside/focusin may close it during Driver focus changes.
    // Reopen only this held menu; teardown removes the observer before closing.
    menuObserver = new MutationObserver(ensureMenu)
    menuObserver.observe(menu, { subtree: true, attributes: true, attributeFilter: ['style', 'aria-expanded'] })
    menu.dispatchEvent(new CustomEvent('guided-tour-menu', { detail: { open: true } }))
    return true
  }

  function positionPopover(presentation) {
    if ((!held || !ensureMenu()) && !list) return
    const popover = document.querySelector('[data-test="first-person.popover"]')
    if (!popover) return
    presentation.refresh?.()
    if (list) {
      const listTop = list.getBoundingClientRect().top
      popover.setAttribute('data-tour-list-popover', '')
      popover.style.width = `${Math.min(600, innerWidth - 24)}px`
      popover.style.maxHeight = `${Math.max(80, listTop - 26)}px`
      popover.style.setProperty('--tour-popover-left', `${Math.max(12, Math.min(list.getBoundingClientRect().left, innerWidth - popover.offsetWidth - 12))}px`)
      popover.style.setProperty('--tour-popover-top', `${Math.max(12, listTop - popover.offsetHeight - 14)}px`)
      return
    }
    const bounds = panel.getBoundingClientRect()
    const beside = bounds.left >= 340
    const width = beside ? Math.min(400, bounds.left - 28) : innerWidth - 24
    popover.setAttribute('data-tour-menu-popover', '')
    popover.style.maxWidth = `${width}px`
    popover.style.maxHeight = `${beside ? innerHeight - 24 : Math.max(80, bounds.top - 26)}px`
    const top = beside
      ? Math.max(12, Math.min(bounds.top, innerHeight - popover.offsetHeight - 12))
      : Math.max(12, bounds.top - popover.offsetHeight - 14)
    popover.style.setProperty('--tour-popover-left', `${beside ? bounds.left - popover.offsetWidth - 14 : 12}px`)
    popover.style.setProperty('--tour-popover-top', `${top}px`)
  }

  function destroy() {
    clearList()
    guardAction(null, null)
    closeMenu()
    if (scroller) {
      if (scrollBehavior) scroller.style.setProperty('scroll-behavior', scrollBehavior, scrollPriority)
      else scroller.style.removeProperty('scroll-behavior')
    }
  }

  return { top, listElement, prepareList, menuTrigger, openMenu, closeMenu, ensureMenu, guardAction, positionPopover, destroy }
}
